import React, { useContext } from "react";
import Login from "./pages/auth/Login";
import { CommonContext } from "./hooks/CommonContext";
import EndInterview from "pages/EndInterview";
import { common } from "utils/constant";
import BotInterviewRoom from "pages/bot/BotInterviewRoom";
import JoinPage from "pages/join-pages";
import RoomLayout from "layout/RoomLayout";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  /* ── Shared full-screen wrapper ── */
  .am-screen {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #080d18;
    font-family: 'Inter', system-ui, sans-serif;
    padding: 24px;
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
  }

  /* ── Animated background orbs ── */
  .am-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    animation: amOrb 8s ease-in-out infinite alternate;
  }

  .am-orb-1 {
    width: 480px; height: 480px;
    background: radial-gradient(circle, rgba(239,68,68,0.12) 0%, transparent 70%);
    top: -120px; left: -80px;
    animation-delay: 0s;
  }

  .am-orb-2 {
    width: 360px; height: 360px;
    background: radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 70%);
    bottom: -80px; right: -60px;
    animation-delay: -4s;
  }

  .am-orb-3 {
    width: 240px; height: 240px;
    background: radial-gradient(circle, rgba(239,68,68,0.07) 0%, transparent 70%);
    bottom: 20%; left: 10%;
    animation-delay: -2s;
  }

  @keyframes amOrb {
    0%   { transform: translate(0, 0) scale(1); }
    100% { transform: translate(30px, 20px) scale(1.08); }
  }

  /* ── Floating particles ── */
  .am-particles {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }

  .am-particle {
    position: absolute;
    width: 2px; height: 2px;
    border-radius: 50%;
    background: rgba(239,68,68,0.4);
    animation: amFloat linear infinite;
  }

  @keyframes amFloat {
    0%   { transform: translateY(100vh) scale(0); opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 0.6; }
    100% { transform: translateY(-10vh) scale(1.5); opacity: 0; }
  }

  /* ── Card ── */
  .am-invalid-card {
    position: relative;
    z-index: 2;
    text-align: center;
    background: rgba(15, 22, 41, 0.85);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(239,68,68,0.15);
    border-radius: 24px;
    padding: 56px 48px;
    max-width: 520px;
    width: 100%;
    box-shadow:
      0 0 0 1px rgba(239,68,68,0.08),
      0 32px 80px rgba(0,0,0,0.6),
      inset 0 1px 0 rgba(255,255,255,0.04);
    animation: amCardIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
  }

  @keyframes amCardIn {
    from { opacity: 0; transform: scale(0.88) translateY(20px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  /* ── Pulsing icon ring ── */
  .am-icon-wrap {
    position: relative;
    width: 96px;
    height: 96px;
    margin: 0 auto 28px;
  }

  .am-icon-pulse {
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 1.5px solid rgba(239,68,68,0.3);
    animation: amPulse 2.4s ease-out infinite;
  }

  .am-icon-pulse-2 {
    position: absolute;
    inset: -18px;
    border-radius: 50%;
    border: 1px solid rgba(239,68,68,0.15);
    animation: amPulse 2.4s ease-out 0.8s infinite;
  }

  @keyframes amPulse {
    0%   { opacity: 1; transform: scale(1); }
    100% { opacity: 0; transform: scale(1.5); }
  }

  .am-icon-inner {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: rgba(239,68,68,0.08);
    border: 1.5px solid rgba(239,68,68,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  /* Broken link SVG icon */
  .am-broken-icon {
    font-size: 44px;
    line-height: 1;
    animation: amShake 4s ease-in-out infinite;
  }

  @keyframes amShake {
    0%, 85%, 100% { transform: rotate(0deg); }
    88%  { transform: rotate(-6deg); }
    91%  { transform: rotate(5deg); }
    94%  { transform: rotate(-4deg); }
    97%  { transform: rotate(3deg); }
  }

  /* ── Text ── */
  .am-error-code {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #f87171;
    margin: 0 0 12px;
    opacity: 0.8;
  }

  .am-invalid-title {
    font-size: clamp(22px, 4vw, 28px);
    font-weight: 800;
    color: #f0f4ff;
    margin: 0 0 14px;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }

  .am-invalid-desc {
    font-size: 14.5px;
    color: #8b9dc3;
    line-height: 1.75;
    margin: 0 0 32px;
    max-width: 380px;
    margin-left: auto;
    margin-right: auto;
  }

  /* ── Divider ── */
  .am-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(239,68,68,0.2), transparent);
    margin: 0 0 24px;
  }

  /* ── Action hint ── */
  .am-hint {
    font-size: 13px;
    color: #6b7fa8;
    line-height: 1.6;
  }

  .am-hint strong {
    color: #c4d0ea;
    font-weight: 600;
  }

  /* ── Loading spinner ── */
  .am-loading-wrap {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    animation: amFadeIn 0.4s ease both;
  }

  @keyframes amFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .am-spinner-wrap {
    position: relative;
    width: 52px;
    height: 52px;
  }

  .am-spinner {
    width: 52px; height: 52px;
    border-radius: 50%;
    border: 2px solid rgba(34,211,238,0.1);
    border-top-color: #22d3ee;
    animation: amSpin 0.9s linear infinite;
    position: absolute;
    inset: 0;
  }

  .am-spinner-inner {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid rgba(34,211,238,0.06);
    border-bottom-color: rgba(34,211,238,0.4);
    animation: amSpin 1.4s linear infinite reverse;
    position: absolute;
    inset: 8px;
  }

  @keyframes amSpin { to { transform: rotate(360deg); } }

  .am-loading-text {
    font-size: 14px;
    font-weight: 500;
    color: #6b7fa8;
    letter-spacing: 0.02em;
  }

  .am-loading-dots::after {
    content: '';
    animation: amDots 1.6s steps(4, end) infinite;
  }

  @keyframes amDots {
    0%  { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

/* Randomly placed floating particles */
const PARTICLE_COUNT = 18;
const Particles = () => (
  <div className="am-particles">
    {Array.from({ length: PARTICLE_COUNT }, (_, i) => (
      <div
        key={i}
        className="am-particle"
        style={{
          left: `${Math.random() * 100}%`,
          width:  `${1 + Math.random() * 2}px`,
          height: `${1 + Math.random() * 2}px`,
          animationDuration: `${6 + Math.random() * 10}s`,
          animationDelay:    `${Math.random() * 8}s`,
          opacity: 0.3 + Math.random() * 0.4,
        }}
      />
    ))}
  </div>
);

/* Broken link emoji rendered as text to avoid icon library dep */
const BrokenLinkIcon = () => (
  <span className="am-broken-icon" role="img" aria-label="broken link">🔗</span>
);

const AppMeeting = () => {
  inject();

  const {
    isInMeetingRoom,
    isLoggedIn,
    isInterviewEnded,
    isInitialLoading,
    interviewDetails,
    isValidUrl,
  } = useContext(CommonContext);

  if (!isValidUrl) {
    return (
      <div className="am-screen">
        <div className="am-orb am-orb-1" />
        <div className="am-orb am-orb-2" />
        <div className="am-orb am-orb-3" />
        <Particles />

        <div className="am-invalid-card">
          {/* Pulsing icon */}
          <div className="am-icon-wrap">
            <div className="am-icon-pulse-2" />
            <div className="am-icon-pulse" />
            <div className="am-icon-inner">
              <BrokenLinkIcon />
            </div>
          </div>

          <p className="am-error-code">Error · Invalid Link</p>

          <h1 className="am-invalid-title">
            This Interview Link<br />Doesn't Exist
          </h1>

          <p className="am-invalid-desc">
            The link you followed is either expired, incorrect, or has
            already been used. Please verify the URL and try again.
          </p>

          <div className="am-divider" />

          <p className="am-hint">
            Need help? Reach out to your recruiter or contact{" "}
            <strong>support@niyantras.com</strong> with your application
            details.
          </p>
        </div>
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="am-screen">
        <div className="am-orb am-orb-2" style={{ opacity: 0.5 }} />
        <div className="am-loading-wrap">
          <div className="am-spinner-wrap">
            <div className="am-spinner" />
            <div className="am-spinner-inner" />
          </div>
          <span className="am-loading-text">
            <span className="am-loading-dots">Loading your interview</span>
          </span>
        </div>
      </div>
    );
  }

  if (isInterviewEnded) return <EndInterview />;

  if (isLoggedIn) {
    if (isInMeetingRoom) {
      return interviewDetails.interviewType === common.agent
        ? <BotInterviewRoom />
        : <RoomLayout />;
    }
    return <JoinPage />;
  }

  return <Login />;
};

export default AppMeeting;